import { getDeviceId } from "@/helpers/device";
import store from "@/store";
import {
  returnObjectIfValueExists,
  getAppSource,
  hasMailSuiteProduct,
  hasSiteProduct,
  isNeoBrand,
  isNeoBusinessDomain,
  isMailSuiteProduct,
  toEpochMillis,
  getAccountId,
  getLoggedInUserRole,
} from "@/helpers";
import { BUNDLE, CUSTOMER, NEO_OFFERING, ORDER } from "@/helpers/const";
import { getProductAttrs } from "@/telemetry/bundleEventHelpers";
import medusa, { toEntityId } from "@/telemetry/medusa";

const getOrderStatusOfCurrentDomain = () => {
  const currentPlanDomainDetails = store.getters["domain/currentProductDetails"];
  return currentPlanDomainDetails ? currentPlanDomainDetails?.status : "";
};

// both cycles are null with order level billing, no bundle level frequency to report
const getBillingFrequency = () => {
  const currentPlan = store.getters.currentPlanDetails;
  return currentPlan.autoRenew
    ? store.getters.bundleRenewalBillingCycle
    : store.getters.bundleBillingCycle;
};

export const isBillingFrequencyChanged = () => {
  return store.getters.bundleBillingCycle !== getBillingFrequency();
};

// Write common medusa params of each here in this file
export const getCommonMedusaParams = () => {
  return {
    order_status: getOrderStatusOfCurrentDomain(),
    device_id: getDeviceId(),
    domain_name: store.getters["domain/domainName"],
    partner_id: +store.getters.partnerId,
    customer_id: +smartStorage.getItem("bllUserId"),
    plan_id: +store.getters["mail/planId"],
    plan_name: smartStorage.getItem("plan"),
    order_id: +store.getters["mail/orderId"],
    user_type: ORDER,
    neo_offering: isNeoBrand()
      ? isNeoBusinessDomain()
        ? NEO_OFFERING.NEO_MANAGED
        : NEO_OFFERING.NEO_CUSTOMER_MANAGED
      : "",
    existing_account_count: +store.getters["currentDomainMailboxDetails"]?.length,
    billing_cycle: store.getters.bundleBillingCycle,
    expiry_date: store.getters.bundleExpiresOn
      ? toEpochMillis(store.getters.bundleExpiresOn)
      : null,
    source: getAppSource(),
    ...(getLoggedInUserRole() === "accountAdmin" && {
      account_id: parseInt(getAccountId()),
    }),
    ...(isBillingFrequencyChanged()
      ? {
          new_billing_cycle: getBillingFrequency(), // FIXME: need to understand where/how this is used
        }
      : {}),
  };
};

/**
 * Mailbox license/count props (mailbox-nudge analytics). Sent only with
 * ORDER-entity events (see sendOrderEvent), not bundle/customer ones.
 * `Number(x) || 0` (not `+x`) so events fired before the mail plan loads
 * carry 0 instead of NaN.
 */
export const getMailboxCountParams = () => ({
  total_mailbox_license_count:
    Number(
      store.getters["mail/currentProductDetails"]?.noOfAccounts?.limit ??
        store.getters["mail/currentProductDetails"]?.noOfAccounts?.purchased
    ) || 0,
  available_mailbox_license_count: Number(store.getters["mail/availableLicenses"]) || 0,
  total_mailbox_count: Number(store.getters["currentDomainMailboxDetails"]?.length) || 0,
});

export const getCommonMedusaParamsForUpgradePlan = plan => {
  return {
    new_plan_id: plan.id,
    new_plan_name: plan.type,
  };
};

export const getCommonMedusaParamsForCustomerEvents = () => {
  return {
    ...getCommonMedusaParams(),
    user_type: CUSTOMER,
    entityId: toEntityId(smartStorage.getItem("bllUserId")),
  };
};

export const getCommonMedusaParamsForBundleEvents = (decorateEvent = true) => {
  return {
    ...(decorateEvent && {
      ...getCommonMedusaParams(),
      ...getProductAttrs(),
      auto_renew: store.getters.currentPlanDetails.autoRenew,
      bundle_id: +store.getters["bundle/bundleId"],
      bundle_status: store.getters.activeProducts.length > 0 ? "active" : "suspended",
      order_count: store.getters.products.length,
      active_order_count: store.getters.activeProducts.length,
    }),
    user_type: BUNDLE,
  };
};

export const getMailProductAttr = (pId, isNewPlan) => {
  const products = store.getters["products"];
  const currentPlanMailDetails = store.getters["mail/currentProductDetails"];
  const planId = pId || currentPlanMailDetails.planId;
  const mailPlans = store.getters["mail/plans"];
  const keyPrefix = isNewPlan ? "mail_new_plan" : "mail_plan";

  if (hasMailSuiteProduct(products)) {
    return {
      [`${keyPrefix}_id`]: planId,
      [`${keyPrefix}_name`]: mailPlans.find(p => p.id === planId)?.type || "unknown",
    };
  }

  return {};
};

export const getSiteProductAttr = (pId, isNewPlan) => {
  const products = store.getters["products"];
  const currentPlanSiteDetails = store.getters["site/currentProductDetails"];
  const planId = pId || currentPlanSiteDetails?.planId;
  const sitePlans = store.getters["site/plans"];
  const keyPrefix = isNewPlan ? "site_new_plan" : "site_plan";

  if (hasSiteProduct(products)) {
    return {
      [`${keyPrefix}_id`]: planId,
      [`${keyPrefix}_name`]: sitePlans.find(p => p.id === planId)?.type || "unknown",
    };
  }

  return {};
};

export const getProductToPurchaseAttr = record => {
  return record
    ? isMailSuiteProduct(record.orderType)
      ? getMailProductAttr(record.id, true)
      : getSiteProductAttr(record.id, true)
    : {};
};

export const getCommonMedusaParamsForBundleBillingEvents = () => {
  const products = store.getters["products"];
  const mailProductDetails = store.getters["mail/currentProductDetails"];
  const currency = store.getters["currency"];
  const billing_country = store.getters["purchaseAndBillingDetails"]?.billing_country;
  const payment_card_type = store.getters["payment/paymentCardType"];

  return {
    ...getMailProductAttr(),
    ...getSiteProductAttr(),
    ...(hasMailSuiteProduct(products)
      ? {
          mailbox_count: mailProductDetails.noOfAccounts.purchased,
        }
      : {}),
    ...returnObjectIfValueExists("payment_card_type", payment_card_type),
    ...returnObjectIfValueExists("billing_country", billing_country),
    currency,
  };
};

/**
 * @param {Object} [options] `{ token }` for callers whose token is not in storage — forwarded to
 * medusa, which then authenticates with it instead of the stored one.
 */
export function sendOrderAndCustomerEvent(event = {}, isCustomerEvent = true, options) {
  this.medusaEvents.trackEvent(
    {
      ...getCommonMedusaParams(),
      ...event,
    },
    options
  );
  isCustomerEvent && isNeoBrand() && sendCustomerEvent(event, options);
}

export function sendCustomerEvent(event = {}, options) {
  medusa.trackEvent(
    {
      ...getCommonMedusaParamsForCustomerEvents(),
      ...event,
      eventName: `${CUSTOMER}_${event.eventName}`,
      /* Keyed by the customer, for the same reason `sendBundleEvent` pins its own user_type —
         callers pass order-scoped params (e.g. `user_type: SITE_ORDER`) straight through. */
      user_type: CUSTOMER,
    },
    options
  );
}

export async function sendBundleEvent(event = {}, decorateEvent = true, options) {
  await medusa.trackEvent(
    {
      ...getCommonMedusaParamsForBundleEvents(decorateEvent),
      ...event,
      /* A bundle event is keyed by the bundle, so this has to win over `event`: callers commonly
         spread in `getCommonMedusaParams()`, which sets `user_type: ORDER`, and that would
         otherwise re-key the event to the mail order. */
      user_type: BUNDLE,
    },
    options
  );
}

/**
 * `sendBundleEvent` for clients that are not logged in yet. Injected into the `preAuthBundleEvents`
 * instance in `bundleEvents.js`, which the login and partner purchase flows use.
 */
export async function sendPreAuthBundleEvent(event = {}, decorateEvent = true) {
  await medusa.trackPreAuthEvent({
    ...getCommonMedusaParamsForBundleEvents(decorateEvent),
    ...event,
    /* Keyed by the bundle — see `sendBundleEvent`. */
    user_type: BUNDLE,
  });
}

export async function sendOrderEvent(event = {}) {
  await medusa.trackEvent({
    ...getCommonMedusaParams(),
    ...getMailboxCountParams(),
    auto_renew: store.getters.currentPlanDetails.autoRenew,
    bundle_id: +store.getters["bundle/bundleId"],
    bundle_status: store.getters.activeProducts.length ? "active" : "suspended",
    order_count: store.getters.products.length,
    active_order_count: store.getters.activeProducts.length,
    ...event,
  });
}

/**
 * @param {Object} [options] `{ token }` for callers whose token is not in storage — the partner
 * purchase flow keeps its token in the `purchaseAuthData` prop.
 */
export function triggerPurchaseAndBillingEvents(restMedusaParams, isCustomerEvent = true, options) {
  const { eventName } = restMedusaParams;
  const billingParams = store.getters.purchaseAndBillingDetails;
  this.medusaEvents.trackEvent(
    {
      ...getCommonMedusaParams(),
      ...billingParams,
      ...restMedusaParams,
    },
    options
  );
  isNeoBrand() &&
    isCustomerEvent &&
    this.medusaEvents.trackEvent(
      {
        ...getCommonMedusaParamsForCustomerEvents(),
        ...billingParams,
        ...restMedusaParams,
        eventName: `${CUSTOMER}_${eventName}`,
      },
      options
    );
}
